import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ProductoApiService } from '../services/producto-api.service';
import { CategoriaProductoApiService } from '../services/categoria-producto-api.service';
import {
  ProductRequest,
  ProductResponse,
  ProductSearchParams,
} from '../models/producto.model';
import { ProductCategoryResponse } from '../models/categoria-producto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ProductoDetailModalComponent } from '../producto-detail-modal/producto-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { formatMoney } from '../../../shared/utils/currency-format.utils';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

@Component({
  selector: 'app-producto-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    ProductoDetailModalComponent,
    HasPermissionDirective,
  ],
  templateUrl: './producto-list.component.html',
  styleUrl: './producto-list.component.css',
})
export class ProductoListComponent implements OnInit {
  productos: ProductResponse[] = [];
  loading = false;
  error = '';
  currentPage = 0;

  // Modal state
  showDetailModal = false;
  selectedProducto: ProductResponse | null = null;

  // Catalogo de categorias para filtro
  categorias: ProductCategoryResponse[] = [];

  ///////////////////////
  //Ordanamiento
  sortBy: string = 'updatedAt_desc';

  // Mapeo de ordenamiento para el backend
  private sortMapping: Record<
    string,
    { sortBy: string; sortDirection: string }
  > = {
    relevance: { sortBy: 'relevance', sortDirection: 'DESC' },
    name_asc: { sortBy: 'name', sortDirection: 'ASC' },
    name_desc: { sortBy: 'name', sortDirection: 'DESC' },
    price_asc: { sortBy: 'price', sortDirection: 'ASC' },
    price_desc: { sortBy: 'price', sortDirection: 'DESC' },
    updatedAt_desc: { sortBy: 'datetimeUpdated', sortDirection: 'DESC' },
    createdAt_asc: { sortBy: 'datetimeCreated', sortDirection: 'ASC' },
    popularity_desc: { sortBy: 'popularity', sortDirection: 'DESC' },
    popularity_asc: { sortBy: 'popularity', sortDirection: 'ASC' },
  };

  onSortChange(): void {
    this.currentPage = 0;
    this.cargarProductos();
  }

  /////////////

  // Filtros
  showFilters = false;
  filtroNombre = '';
  filtroSku = '';
  filtroCategoria: number | undefined;
  filtroTipo: string | undefined;
  /** Inc 53: subtipo de servicio ('resources' | 'consulting'). */
  filtroSubtipo: string | undefined;
  filtroIsActive: boolean | undefined;
  filtroStatus: string | undefined = undefined;
  filtroMinPrecio: number | undefined;
  filtroMaxPrecio: number | undefined;
  filtroBillingCycle: string | undefined;
  filtroHasVariants: boolean | undefined;
  filtroUsesTierPricing: boolean | undefined;
  filtroOwner: string = 'todos';

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'producto-list-page-size';
  private readonly VIEW_MODE_KEY = 'producto-list-view-mode';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;
  viewMode: 'table' | 'grid' =
    (localStorage.getItem(this.VIEW_MODE_KEY) as 'table' | 'grid') || 'table';

  constructor(
    private productApi: ProductoApiService,
    private categoriaApi: CategoriaProductoApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.cargarCategorias();
    this.cargarProductos();
  }

  private cargarCategorias(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.categoriaApi.searchProductCategory({ businessId }).subscribe({
      next: (data) => (this.categorias = data),
      error: () => {},
    });
  }

  cargarProductos(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Obtener configuración de ordenamiento actual
    const sortConfig =
      this.sortMapping[this.sortBy] || this.sortMapping['relevance'];

    const params: ProductSearchParams = {
      businessId,
      searchName: this.filtroNombre || undefined,
      searchSku: this.filtroSku || undefined,
      idCategory: this.filtroCategoria,
      type: this.filtroTipo,
      subtype: this.filtroSubtipo,
      isActive: this.filtroIsActive,
      status: this.filtroStatus || undefined,
      minPrice: this.normalizeNumberFilter(this.filtroMinPrecio),
      maxPrice: this.normalizeNumberFilter(this.filtroMaxPrecio),
      billingCycle: this.filtroBillingCycle,
      hasVariants: this.filtroHasVariants,
      usesTierPricing: this.filtroUsesTierPricing,
      page: this.globalPage,
      size: this.pageSize,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
    };
    this.productApi.searchProduct(params).subscribe({
      next: (response) => {
        this.productos = response.content;
        this.globalPage = response.number ?? this.globalPage;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar productos';
        this.loading = false;
        console.error('Error:', err);
      },
    });
  }

  private normalizeNumberFilter(value: any): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const num = Number(value);
    if (isNaN(num) || num < 0) return undefined;
    return num;
  }

  /**
   * Filtros rápidos por tipo. Inc 53: los servicios se pueden acotar además por
   * subtipo ('resources' = Recursos, 'consulting' = Consultoría).
   */
  AllProducts(tipo: string): void {
    this.filtroOwner = tipo;
    this.currentPage = 0;
    this.globalPage = 0;
    this.filtroSubtipo = undefined;

    // filtro según el tipo seleccionado
    if (tipo === 'conInventario') {
      this.filtroTipo = 'inventory';
    } else if (tipo === 'sinInventario') {
      this.filtroTipo = 'no_inventory';
    } else if (tipo === 'servicios') {
      this.filtroTipo = 'service';
    } else if (tipo === 'recursos') {
      this.filtroTipo = 'service';
      this.filtroSubtipo = 'resources';
    } else if (tipo === 'consultoria') {
      this.filtroTipo = 'service';
      this.filtroSubtipo = 'consulting';
    } else {
      this.filtroTipo = undefined;
    }
    this.cargarProductos();
  }

  buscar(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarProductos();
  }

  limpiarFiltro(): void {
    this.filtroNombre = '';
    this.filtroSku = '';
    this.filtroCategoria = undefined;
    this.filtroTipo = undefined;
    this.filtroSubtipo = undefined;
    this.filtroOwner = 'todos';
    this.filtroIsActive = undefined;
    this.filtroStatus = undefined;
    this.filtroMinPrecio = undefined;
    this.filtroMaxPrecio = undefined;
    this.filtroBillingCycle = undefined;
    this.filtroHasVariants = undefined;
    this.filtroUsesTierPricing = undefined;
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarProductos();
  }

  nuevoProducto(): void {
    this.router.navigate(['/productos/crear']);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  editarProducto(producto: ProductResponse): void {
    this.router.navigate(['/productos/editar', producto.id]);
  }

  async eliminarProducto(producto: ProductResponse): Promise<void> {
    if (!producto.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar producto',
      message: `¿Está seguro de eliminar el producto "${producto.name}"?`,
    });
    if (!ok) return;
    this.productApi.deleteProduct(producto.id, businessId).subscribe({
      next: () => {
        this.cargarProductos();
        this.toast.deleted('Producto');
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar el producto',
        });
      },
    });
  }

  // Metodos auxiliares para badges y formato
  getCategoryName(producto: ProductResponse): string {
    if (producto.categoryName) return producto.categoryName;
    if (!producto.idCategory) return 'Sin categoría';
    const cat = this.categorias.find((c) => c.id === producto.idCategory);
    return cat?.name || 'Sin categoría';
  }

  /**
   * Inc 53: etiqueta del tipo de dos niveles. Para servicios muestra además el
   * subtipo ("Servicio · Consultoría"); para productos, el manejo de inventario.
   */
  getTipoLabel(producto: ProductResponse): string {
    if (producto.type === 'service') {
      if (producto.subtype === 'resources') return 'Servicio · Recursos';
      if (producto.subtype === 'consulting') return 'Servicio · Consultoría';
      return 'Servicio';
    }
    return producto.type === 'no_inventory'
      ? 'Prod. sin inventario'
      : 'Producto';
  }

  getActiveColor(producto: ProductResponse): BadgeColor {
    switch (producto.status) {
      case 'draft':
        return 'warning';
      case 'active':
        return 'success';
      case 'discontinued':
        return 'error';
      case 'archived':
        return 'info';
      default:
        return 'primary';
    }
  }

  getActiveLabel(producto: ProductResponse): string {
    switch (producto.status) {
      case 'draft':
        return 'Borrador';
      case 'active':
        return 'Activo';
      case 'discontinued':
        return 'Descontinuado';
      case 'archived':
        return 'Archivado';
      default:
        return '-';
    }
  }

  /**
   * ¿Se debe mostrar el precio promocional en lugar del de lista? Solo cuando la
   * promoción está VIGENTE, ENCENDIDA y además tiene precio promocional.
   * `promoVigente` solo mira las fechas: con la vigencia cargada pero sin
   * `promoPrice` (o con el interruptor apagado) el listado tachaba el precio real
   * y mostraba "-" como precio actual.
   */
  tienePromoActiva(producto: ProductResponse): boolean {
    return !!(
      producto.promoVigente &&
      producto.promoEnabled !== false &&
      producto.promoPrice !== undefined &&
      producto.promoPrice !== null
    );
  }

  formatPrecio(price?: number, prefix?: string, currencyCode?: string): string {
    if (price === undefined || price === null) return '-';
    // La rama del prefijo formateaba a 2 decimales fijos e ignoraba la moneda:
    // en yenes salía "¥ 1.560,25" y en dinares se comía el tercer decimal.
    // formatMoney usa la unidad mínima ISO 4217 del código.
    if (prefix) {
      return formatMoney(price, prefix, currencyCode);
    }
    return currencyCode
      ? new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: currencyCode,
        }).format(price)
      : formatMoney(price, null, null);
  }

  // Paginación
  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarProductos();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }
  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }
  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }
  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  verDetalle(producto: ProductResponse): void {
    this.selectedProducto = producto;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedProducto = null;
  }

  get hasPreviousPage(): boolean {
    return this.currentPage > 0;
  }

  setViewMode(mode: 'table' | 'grid'): void {
    this.viewMode = mode;
    localStorage.setItem(this.VIEW_MODE_KEY, mode);
  }

  changeProductStatus(p: ProductResponse, status: string): void {
    if (!p.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: ProductRequest = {
      ...p,
      businessId,
      status,
    };
    this.productApi.updateProduct(p.id, businessId, request).subscribe({
      next: () => {
        this.toast.success({
          title: 'Producto',
          message: 'Se cambio el estado del producto',
        });
        this.cargarProductos();
      },
      error: (err) => {
        this.toast.error({
          title: 'Producto',
          message:
            err.error?.message || 'Error al cambiar el estado del producto',
        });
      },
    });
  }

  filterArchived() {
    this.filtroStatus =
      this.filtroStatus !== 'archived' ? 'archived' : undefined;
    this.cargarProductos();
  }
}
